import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface OnBehalfCustomer {
  id: number;
  firstName: string;
  lastName: string;
  companyName: string | null;
  shippingAddress: string | null;
}

interface OrderOnBehalfContextType {
  customer: OnBehalfCustomer | null;
  startOrdering: (customer: OnBehalfCustomer) => Promise<void>;
  stopOrdering: () => void;
  isOrdering: boolean;
}

const STORAGE_KEY = "orderOnBehalf";

function loadFromStorage(): OnBehalfCustomer | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === "number") return parsed;
  } catch {}
  return null;
}

const OrderOnBehalfContext = createContext<OrderOnBehalfContextType>({
  customer: null,
  startOrdering: async () => {},
  stopOrdering: () => {},
  isOrdering: false,
});

export function OrderOnBehalfProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<OnBehalfCustomer | null>(loadFromStorage);

  const startOrdering = useCallback(async (c: OnBehalfCustomer) => {
    setCustomer(c);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  }, []);

  const stopOrdering = useCallback(() => {
    setCustomer(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <OrderOnBehalfContext.Provider value={{ customer, startOrdering, stopOrdering, isOrdering: !!customer }}>
      {children}
    </OrderOnBehalfContext.Provider>
  );
}

export function useOrderOnBehalf() {
  return useContext(OrderOnBehalfContext);
}
